import {
  BellOutlined,
  GlobalOutlined,
  LogoutOutlined,
  MoonOutlined,
  SearchOutlined,
  SettingOutlined,
  SlidersOutlined,
  SunOutlined,
  SyncOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Avatar, Badge, Button, Dropdown, type MenuProps, Popover, theme } from 'antd';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PressRipple } from '@/components/PressRipple';
import { ThemeIcon } from '@/components/ThemeIcon';
import type { ThemeMode } from '@/config/preferences';
import type { AppMenuItem } from '@/config/types';
import { useChangeLocale } from '@/hooks/useChangeLocale';
import { useShellText } from '@/hooks/useShellText';
import { localeOptions } from '@/locales/config';
import { getIntl } from '@/locales/intl';
import { NotificationList } from '@/pages/account/notifications/NotificationList';
import { useNotifications } from '@/pages/account/shared/queries';
import { useAuthStore } from '@/stores/authStore';
import { useLayoutSettingsStore } from '@/stores/layoutSettingsStore';
import { type AppLocale, useLocaleStore } from '@/stores/localeStore';
import { setThemeWithTransition } from '@/utils/themeTransition';
import { CommandPalette } from './CommandPalette';

type Overlay = 'search' | 'language' | 'theme' | 'notification' | 'user' | null;
export function HeaderActions({
  isPhone,
  menuItems,
  onNavigate,
  onPreferences,
  onLogout,
  preferencesOpen = false,
}: {
  isPhone: boolean;
  menuItems: AppMenuItem[];
  onNavigate: (path: string, onApproved?: () => void) => void;
  onPreferences: () => void;
  onLogout: () => Promise<void>;
  preferencesOpen?: boolean;
}) {
  const t = useShellText();
  const { token } = theme.useToken();
  const currentUser = useAuthStore((state) => state.currentUser);
  const locale = useLocaleStore((state) => state.locale);
  const setLocale = useChangeLocale();
  const settings = useLayoutSettingsStore((state) => state.settings);
  const query = useNotifications({ page: 1, pageSize: 6 });
  const [overlay, setOverlay] = useState<Overlay>(null);
  const [subMenuKeys, setSubMenuKeys] = useState<string[]>([]);
  const menuTrigger = useRef<HTMLElement | null>(null);
  const notificationTrigger = useRef<HTMLButtonElement>(null);
  const notificationPanel = useRef<HTMLElement>(null);
  const notificationPanelId = useId();
  const [loggingOut, setLoggingOut] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (location.key) setOverlay(null);
  }, [location.key]);
  useEffect(() => {
    function shortcut(event: KeyboardEvent) {
      if (
        !preferencesOpen &&
        !event.isComposing &&
        !event.repeat &&
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === 'k'
      ) {
        event.preventDefault();
        setOverlay((value) => (value === 'search' ? null : 'search'));
      }
      if (event.key === 'Escape' && overlay) {
        if (overlay === 'notification') notificationTrigger.current?.focus();
        if (['language', 'theme', 'user'].includes(overlay))
          menuTrigger.current?.focus();
        setOverlay(null);
      }
    }
    document.addEventListener('keydown', shortcut);
    return () => document.removeEventListener('keydown', shortcut);
  }, [preferencesOpen, overlay]);
  const buttonStyle = {
    width: isPhone ? 44 : 36,
    height: isPhone ? 44 : 36,
    borderRadius: 8,
    color: token.colorTextSecondary,
  };
  const localeItems = localeOptions.map(({ key, label, emoji }) => ({
    key: `locale:${key}`,
    label,
    icon: <span aria-hidden>{emoji}</span>,
  }));
  const themeItems = [
    { key: 'theme:system', label: t('跟随系统', 'System'), icon: <SyncOutlined /> },
    { key: 'theme:light', label: t('浅色', 'Light'), icon: <SunOutlined /> },
    { key: 'theme:dark', label: t('深色', 'Dark'), icon: <MoonOutlined /> },
  ];
  const selectedKeys = [`locale:${locale}`, `theme:${settings.themeMode}`];
  function navigate(path: string, onApproved?: () => void) {
    onNavigate(path, () => {
      setOverlay(null);
      onApproved?.();
    });
  }
  const handleMenu: MenuProps['onClick'] = ({ key, domEvent }) => {
    if (key.startsWith('locale:')) {
      setOverlay(null);
      setLocale(key.slice(7) as AppLocale);
    } else if (key.startsWith('theme:')) {
      setOverlay(null);
      const rect = menuTrigger.current?.getBoundingClientRect();
      const pointer = 'clientX' in domEvent && (domEvent.clientX || domEvent.clientY);
      setThemeWithTransition(key.slice(6) as ThemeMode, {
        x: pointer ? domEvent.clientX : (rect?.left ?? 0) + (rect?.width ?? 0) / 2,
        y: pointer ? domEvent.clientY : (rect?.top ?? 0) + (rect?.height ?? 0) / 2,
      });
    } else if (key === 'preferences') {
      setOverlay(null);
      onPreferences();
    } else if (key === 'logout') {
      setLoggingOut(true);
      void onLogout().finally(() => setLoggingOut(false));
    } else {
      navigate(key);
    }
  };
  function popup(name: Exclude<Overlay, null>, items: MenuProps['items']) {
    return {
      open: overlay === name,
      destroyOnHidden: true,
      popupRender: (menu: ReactNode) => <PressRipple>{menu}</PressRipple>,
      onOpenChange: (open: boolean) => {
        if (open) {
          setSubMenuKeys([]);
          menuTrigger.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
        }
        setOverlay((value) => (open ? name : value === name ? null : value));
      },
      trigger: ['click'] as ['click'],
      placement: 'bottomRight' as const,
      menu: {
        items,
        selectedKeys,
        openKeys: overlay === name ? subMenuKeys : [],
        onOpenChange: (keys: string[]) => setSubMenuKeys(keys.slice(-1)),
        onClick: handleMenu,
        triggerSubMenuAction: 'click' as const,
        // 手机两侧都不足时，除了自动翻转还须在视口内平移；保留原生子菜单与键盘交互。
        builtinPlacements: isPhone
          ? {
              rightTop: {
                points: ['tl', 'tr'],
                overflow: { adjustX: true, adjustY: true, shiftX: true, shiftY: true },
              },
              leftTop: {
                points: ['tr', 'tl'],
                overflow: { adjustX: true, adjustY: true, shiftX: true, shiftY: true },
              },
            }
          : undefined,
      },
      styles: { root: { minWidth: 164, maxWidth: 'calc(100vw - 16px)' } },
    };
  }
  const count = query.isError ? '!' : query.data?.unreadCount;
  const notificationLabel = query.isError
    ? t('通知，加载失败', 'Notifications, failed to load')
    : query.data
      ? t('通知，{count} 条未读', 'Notifications, {count} unread', {
          count: query.data.unreadCount,
        })
      : t('通知，加载中', 'Notifications, loading');
  return (
    <>
      <section
        aria-label={t('头部操作', 'Header actions')}
        style={{ display: 'flex', alignItems: 'center', gap: 4 }}
      >
        <Button
          data-press-ripple-target
          type="text"
          style={buttonStyle}
          icon={<SearchOutlined />}
          aria-label={t('搜索菜单', 'Search menus')}
          title={isPhone ? undefined : 'Ctrl / ⌘ K'}
          onClick={() => setOverlay('search')}
        />
        {!isPhone && (
          <>
            <Dropdown {...popup('language', localeItems)}>
              <Button
                data-press-ripple-target
                type="text"
                style={buttonStyle}
                icon={<GlobalOutlined />}
                aria-label={t('切换语言', 'Change language')}
              />
            </Dropdown>
            <Dropdown {...popup('theme', themeItems)}>
              <Button
                data-press-ripple-target
                type="text"
                style={buttonStyle}
                icon={<ThemeIcon />}
                aria-label={t('切换主题', 'Change theme')}
              />
            </Dropdown>
          </>
        )}
        <Popover
          open={overlay === 'notification'}
          trigger="click"
          placement={isPhone ? 'bottom' : 'bottomRight'}
          align={{ overflow: { adjustX: true, adjustY: true, shiftX: true } }}
          arrow={!isPhone}
          styles={{
            root: isPhone
              ? { width: '100vw', paddingInline: 12, boxSizing: 'border-box' }
              : { width: 364 },
          }}
          destroyOnHidden
          afterOpenChange={(open) => {
            if (open) notificationPanel.current?.focus();
          }}
          onOpenChange={(open) =>
            setOverlay((value) =>
              open ? 'notification' : value === 'notification' ? null : value,
            )
          }
          title={t('通知', 'Notifications')}
          content={
            <section
              id={notificationPanelId}
              ref={notificationPanel}
              aria-label={t('通知预览', 'Notification preview')}
              tabIndex={-1}
              style={{
                maxHeight: 'calc(100dvh - 120px)',
                display: 'flex',
                flexDirection: 'column',
                outline: 'none',
              }}
            >
              <NotificationList query={query} preview />
              <Button
                type="link"
                block
                style={{ flexShrink: 0, minHeight: isPhone ? 44 : undefined }}
                onClick={() => navigate('/account/notifications')}
              >
                {t('查看全部通知', 'View all notifications')}
              </Button>
            </section>
          }
        >
          <Button
            ref={notificationTrigger}
            aria-expanded={overlay === 'notification'}
            aria-controls={overlay === 'notification' ? notificationPanelId : undefined}
            data-press-ripple-target
            type="text"
            // 保留多位未读角标；波纹本身由内部 layer 裁切。
            style={{ ...buttonStyle, overflow: 'visible' }}
            aria-label={notificationLabel}
          >
            <Badge count={count} size="small" offset={[3, -2]}>
              <BellOutlined />
            </Badge>
          </Button>
        </Popover>
        <Dropdown
          {...popup('user', [
            {
              key: '/account/notifications',
              icon: <BellOutlined />,
              label: t('通知中心', 'Notification center'),
            },
            {
              key: '/account/center',
              icon: <UserOutlined />,
              label: getIntl().formatMessage({ id: 'menu.account.center' }),
            },
            {
              key: '/account/settings',
              icon: <SettingOutlined />,
              label: getIntl().formatMessage({ id: 'menu.account.settings' }),
            },
            {
              key: 'preferences',
              icon: <SlidersOutlined />,
              label: t('偏好设置', 'Preferences'),
            },
            ...(isPhone
              ? [
                  {
                    key: 'languages',
                    icon: <GlobalOutlined />,
                    label: t('切换语言', 'Change language'),
                    children: localeItems,
                  },
                  {
                    key: 'themes',
                    icon: <ThemeIcon />,
                    label: t('主题模式', 'Theme mode'),
                    children: themeItems,
                  },
                ]
              : []),
            { type: 'divider' },
            {
              key: 'logout',
              icon: <LogoutOutlined />,
              label: t('退出', 'Sign out'),
              danger: true,
              disabled: loggingOut,
            },
          ])}
        >
          <Button
            data-press-ripple-target
            type="text"
            aria-label={t('用户菜单', 'User menu')}
            style={{
              height: isPhone ? 44 : 36,
              minWidth: isPhone ? 44 : undefined,
              borderRadius: 8,
              paddingInline: 8,
              maxWidth: 168,
              color: token.colorTextTertiary,
            }}
            loading={loggingOut}
          >
            <Avatar size={26} src={currentUser?.avatar} icon={<UserOutlined />} />
            {!isPhone && (
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {currentUser?.name ?? t('用户', 'User')}
              </span>
            )}
          </Button>
        </Dropdown>
      </section>
      <CommandPalette
        isPhone={isPhone}
        open={overlay === 'search'}
        items={menuItems}
        onClose={() => setOverlay(null)}
        onNavigate={navigate}
      />
    </>
  );
}
